import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MonthNav from "@/components/MonthNav";
import {
  AlertCircle, ExternalLink, ChevronRight, Users, BookOpen, User as UserIcon, Flame, Receipt, Sparkles, ShieldAlert, Upload,
} from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

export default function ProviderHome() {
  const { user } = useAuth();
  const isRespite = user?.role === "respite";
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [clients, setClients] = useState(null); // null = loading, [] = no assignments, [...] = ready
  const [activeClientId, setActiveClientId] = useState("");
  const [cal, setCal] = useState(null);
  const [resources, setResources] = useState([]);
  const [links, setLinks] = useState({
    incident_report_url: "",
    pba_receipt_url: "",
    doc_hub_base_url: "",
    dspd_safety_url: "",
  });
  const [voiceCount, setVoiceCount] = useState(null); // null = unknown, int = count
  const [lockout, setLockout] = useState(null);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0) {
        const saved = localStorage.getItem("oppo_active_client");
        const initial = data.find((c) => c.id === saved) || data[0];
        setActiveClientId(initial.id);
      }
    }).catch(() => setClients([]));
    api.get("/resources").then(({ data }) => setResources(data));
    api.get("/system-links").then(({ data }) => setLinks(data)).catch(() => {});
    if (!isRespite) {
      api.get("/me/voice-strength").then(({ data }) => setVoiceCount(Number(data?.count || 0))).catch(() => {});
    }
    api.get("/me/quarterly-drill-lockout").then(({ data }) => setLockout(data)).catch(() => setLockout({ locked: false, missing: [] }));
  }, [isRespite]);

  useEffect(() => {
    if (!activeClientId) return;
    localStorage.setItem("oppo_active_client", activeClientId);
    api.get(`/calendar-status?client_id=${activeClientId}&year=${year}&month=${month}`).then(({ data }) => setCal(data));
  }, [activeClientId, year, month]);

  const activeClient = clients ? clients.find((c) => c.id === activeClientId) : null;
  const clientsLoading = clients === null;
  const clientsEmpty = clients !== null && clients.length === 0;
  const noteSet = new Set(cal?.note_dates || []);
  const greenSet = new Set(cal?.green_dates || []);
  const blackSet = new Set(cal?.black_dates || []);
  const emarSet = new Set(cal?.emar_dates || []);
  const respiteSet = new Set(cal?.respite_dates || []);
  const hasMeds = cal?.has_medications;

  const firstDow = cal ? new Date(year, month - 1, 1).getDay() : 0;
  const cells = [];
  if (cal) {
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= cal.total_days; d++) cells.push(d);
  }
  const today = new Date();

  return (
    <div className="space-y-5" data-testid="provider-dashboard">
      {/* Non-blocking Evacuation Drill Reminder Banner */}
      {lockout?.missing?.length > 0 && (
        <div 
          className="mb-6 p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 shadow-sm flex items-center justify-between gap-4"
          data-testid="lockout-reminder-banner"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 text-amber-800">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <p className="font-bold text-sm text-amber-900">Evacuation Drill Reminder</p>
              <p className="text-xs text-amber-800">
                Missing quarters: {lockout.missing.map(m => m.label).join(", ")}. Please log an evacuation drill when possible.
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
            onClick={() => navigate("/provider/evacuation-drill")}
            data-testid="lockout-go-drill"
          >
            <Flame className="w-4 h-4 mr-1.5" /> Log Drill
          </Button>
        </div>
      )}
      {/* v18 — Voice Strength tier badge: ALWAYS visible (not just at <2). Shows the
          contractor where they are on the voice fingerprint readiness curve. */}
      {voiceCount !== null && (() => {
        const tier = voiceCount >= 5
          ? { name: "Strong", tone: "emerald", subtitle: "Full 5-sample voice fingerprint — your AI summaries match your writing voice with peak accuracy." }
          : voiceCount >= 2
            ? { name: "Active", tone: "amber", subtitle: `Voice fingerprint engaged · ${5 - voiceCount} more signed note${5 - voiceCount === 1 ? "" : "s"} until peak Strong tier.` }
            : { name: "Building", tone: "slate", subtitle: voiceCount === 0
                ? "Sign 2 substantive narratives to activate AI voice matching — future notes will read like you wrote them."
                : "1 more signed narrative will activate AI voice matching." };
        const styles = {
          emerald: "border-emerald-200 from-emerald-50 to-emerald-100 text-emerald-900 [&_.tier-pill]:bg-emerald-200 [&_.tier-pill]:text-emerald-900",
          amber: "border-amber-200 from-amber-50 to-orange-50 text-amber-900 [&_.tier-pill]:bg-amber-200 [&_.tier-pill]:text-amber-900",
          slate: "border-slate-200 from-slate-50 to-slate-100 text-slate-900 [&_.tier-pill]:bg-slate-200 [&_.tier-pill]:text-slate-900",
        }[tier.tone];
        return (
          <div
            className={`rounded-lg border-2 bg-gradient-to-r p-4 flex items-start gap-3 ${styles}`}
            data-testid="voice-strength-card"
          >
            <div className="h-10 w-10 rounded-md bg-white/70 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-70">Voice Fingerprint Strength</p>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display font-bold leading-tight" data-testid="voice-tier-label">
                  {tier.name} <span className="opacity-60 font-normal text-sm">·</span> <span data-testid="voice-count">{voiceCount}</span> signed narrative{voiceCount === 1 ? "" : "s"}
                </p>
                <span
                  className="tier-pill inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                  data-testid={`voice-tier-${tier.name.toLowerCase()}`}
                >
                  {tier.name === "Strong" ? "Peak" : tier.name === "Active" ? "Engaged" : "Building"}
                </span>
              </div>
              <p className="text-xs mt-1 opacity-90">{tier.subtitle}</p>
            </div>
          </div>
        );
      })()}

      {/* v25 — External document intake (Google Form) — admin-configurable via REACT_APP_DOCS_FORM_URL env */}
      {(process.env.REACT_APP_DOCS_FORM_URL || "").trim() && (
        <a
          href={process.env.REACT_APP_DOCS_FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg p-3 shadow-sm border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 transition-all flex items-center gap-3 tap-target"
          data-testid="docs-upload-link"
        >
          <div className="h-10 w-10 rounded-md bg-amber-200 flex items-center justify-center shrink-0">
            <Upload className="w-5 h-5 text-amber-800" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">External intake</p>
            <p className="font-display font-bold leading-tight text-amber-900">📤 Upload Documents &amp; Records</p>
          </div>
          <ExternalLink className="w-4 h-4 text-amber-700" />
        </a>
      )}

      {/* Layout: Incident Report on the left of active client list */}
      <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr] gap-3">
        <a
          href={links.incident_report_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg p-4 shadow-sm border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors tap-target flex items-center gap-3"
          style={{ color: "#334155" }}
          data-testid="incident-report-link"
        >
          <div className="h-11 w-11 rounded-md flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5" style={{ color: "#334155" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#475569" }}>Urgent</p>
            <p className="font-display font-bold leading-tight" style={{ color: "#334155" }}>Submit Incident Report</p>
          </div>
          <ExternalLink className="w-4 h-4" style={{ color: "#334155" }} />
        </a>

        <Card className="p-3" data-testid="active-client-list">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-primary" />
            <p className="text-label">Active Client</p>
          </div>
          {clientsLoading ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="active-client-loading">Loading your assigned clients…</p>
          ) : clientsEmpty ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="active-client-empty">
              No clients are currently assigned to you. Reach out to your admin to be paired with a client roster.
            </p>
          ) : (
            <Select value={activeClientId || undefined} onValueChange={setActiveClientId}>
              <SelectTrigger className="h-11" data-testid="active-client-trigger">
                <SelectValue placeholder="Select a client…" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`active-client-option-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {activeClient && (
            <Link
              to={`/provider/client/${activeClient.id}`}
              className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              data-testid="active-client-profile-link"
            >
              {activeClient.name.split(/\s+/)[0]}&apos;s Profile
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </Card>
      </div>

      <Card className="p-4 space-y-3" data-testid="provider-month-grid">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-label">Submission Calendar</p>
            <h2 className="font-display font-bold text-xl" data-testid="submission-calendar-heading">
              {activeClient
                ? `${activeClient.name.split(/\s+/)[0]}'s Daily Logs Calendar`
                : "Daily Logs Calendar"}
            </h2>
          </div>
          {activeClient && (
            <MonthNav year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} testId="provider-month-nav" />
          )}
        </div>

        {!activeClient ? (
          <div className="rounded-md border-2 border-dashed border-border bg-secondary/30 p-8 text-center" data-testid="submission-calendar-cta">
            <Users className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="font-display font-semibold text-base">
              {clientsEmpty
                ? "No clients assigned yet."
                : "Please select an active client above to view your documentation calendar."}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Once selected, you&apos;ll see every day&apos;s documentation status — green for stayed-overnight, black for documented absences, red for missing.
            </p>
          </div>
        ) : cal && (
          <>
            <div className="grid grid-cols-7 gap-1">
              {["S","M","T","W","T","F","S"].map((d, i) => (
                <p key={`dow-${i}`} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-center py-1">{d}</p>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="provider-calendar-grid">
              {cells.map((d, i) => {
                if (d == null) return <div key={`empty-${i}`} />;
                const dateStr = `${year}-${pad(month)}-${pad(d)}`;
                const hasNote = noteSet.has(dateStr);
                const isGreen = greenSet.has(dateStr);
                const isBlack = blackSet.has(dateStr);
                const hasEmar = emarSet.has(dateStr);
                const isRespiteDay = respiteSet.has(dateStr);
                const dateObj = new Date(year, month - 1, d);
                const isFuture = dateObj > today;
                // Note completeness for the dual-required test: note + emar (if meds exist)
                const noteAndMedsOk = hasNote && (!hasMeds || hasEmar);
                // Tri-state: green / black / red. Respite for primary contractor view => gray.
                let cls;
                if (isRespiteDay && !isRespite) {
                  cls = "bg-slate-400 border-slate-500 text-white";
                } else if (noteAndMedsOk && isGreen) {
                  cls = "bg-emerald-500 border-emerald-600 text-white";
                } else if (noteAndMedsOk && isBlack) {
                  cls = "bg-slate-900 border-slate-950 text-white";
                } else if (isFuture) {
                  cls = "bg-white border-border text-muted-foreground";
                } else {
                  cls = "bg-red-500 border-red-600 text-white";
                }
                const isToday = year === today.getFullYear() && month === today.getMonth() + 1 && d === today.getDate();
                const disabled = isRespiteDay && !isRespite;
                return (
                  <button
                    key={dateStr}
                    disabled={disabled}
                    title={disabled ? "Client in Respite — primary contractor is locked out for this date" : undefined}
                    onClick={() => navigate(`/provider/daily-note?client=${activeClientId}&date=${dateStr}`)}
                    className={`aspect-square rounded-md border-2 text-sm font-semibold transition-transform ${disabled ? "cursor-not-allowed opacity-90" : "hover:scale-105"} ${cls} ${isToday ? "ring-2 ring-primary ring-offset-1" : ""}`}
                    data-testid={`provider-cal-day-${d}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-emerald-600 bg-emerald-500" /> Stayed overnight (billable)</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-slate-950 bg-slate-900" /> Did not stay (documented)</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-red-600 bg-red-500" /> Missing / past due</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-border bg-white" /> Future</span>
              {!isRespite && (
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border-2 border-slate-500 bg-slate-400" /> Client in Respite</span>
              )}
            </div>
          </>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-primary" />
          <p className="text-label">Resource Hub</p>
        </div>
        <div className="space-y-2">
          {resources.map((r) => (
            <a
              key={r.id} href={r.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between p-3 rounded-md bg-secondary hover:bg-secondary/70 transition-colors"
              data-testid={`resource-${r.id}`}
            >
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{r.title}</p>
                {r.description && <p className="text-xs text-muted-foreground truncate">{r.description}</p>}
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
          ))}
        </div>
      </Card>

      {!isRespite && (
        <Card className="p-4 border-2 border-red-200">
          <button
            onClick={() => navigate("/provider/evacuation-drill")}
            className="w-full flex items-center gap-3 tap-target"
            data-testid="log-evacuation-drill"
          >
            <div className="h-11 w-11 rounded-md bg-red-100 text-red-700 flex items-center justify-center">
              <Flame className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-red-700">Quarterly Compliance</p>
              <p className="font-display font-bold leading-tight">Log Evacuation Drill</p>
              <p className="text-xs text-muted-foreground">One submission marks the drill complete for every client in this home.</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </Card>
      )}

      {!isRespite && (
        <a
          href={links.pba_receipt_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-lg p-4 border border-slate-200 bg-white hover:bg-slate-50 transition-colors tap-target"
          style={{ color: "#334155" }}
          data-testid="pba-receipt-link"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
              <Receipt className="w-5 h-5" style={{ color: "#334155" }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#64748B" }}>Monthly Financial Compliance</p>
              <p className="font-display font-bold leading-tight" style={{ color: "#334155" }}>
                <span className="mr-1.5" aria-hidden="true">🧾</span>
                Submit PBA Receipt
              </p>
            </div>
            <ExternalLink className="w-4 h-4" style={{ color: "#334155" }} />
          </div>
        </a>
      )}
    </div>
  );
}
